import React, { createContext, useContext, useState, useMemo } from 'react'
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  CssBaseline,
  PaletteMode,
} from '@mui/material'

interface ThemeContextValue {
  mode: PaletteMode
  toggleMode: () => void
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'light',
  toggleMode: () => {},
})

export const useThemeMode = () => useContext(ThemeContext)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<PaletteMode>(() => {
    return (localStorage.getItem('themeMode') as PaletteMode) ?? 'light'
  })

  const toggleMode = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light'
      localStorage.setItem('themeMode', next)
      return next
    })
  }

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#168FCA', dark: '#0C6E9F', light: '#58B6E2',
            50: '#EAF7FD', 100: '#CDECF9', 200: '#9EDAF2',
          },
          secondary: { main: '#F3A51C', dark: '#C77A08', light: '#F9C968' },
          success: { main: '#0E9F6E' },
          warning: { main: '#E98913' },
          error: { main: '#E5484D' },
          info: { main: '#168FCA' },
          background: {
            default: mode === 'light' ? '#F4F7FB' : '#09111F',
            paper: mode === 'light' ? '#FFFFFF' : '#111C2E',
          },
          text: {
            primary: mode === 'light' ? '#102A43' : '#F1F5F9',
            secondary: mode === 'light' ? '#62758A' : '#A8B6C7',
          },
          divider: mode === 'light' ? '#E3EAF2' : '#24344A',
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h4: { fontWeight: 800 },
          h5: { fontWeight: 750 },
          h6: { fontWeight: 700 },
          button: { fontWeight: 700 },
        },
        shape: { borderRadius: 12 },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: { backgroundImage: mode === 'light' ? 'radial-gradient(circle at 100% 0%, rgba(22,143,202,0.055), transparent 28rem)' : 'none' },
              '*': { scrollbarWidth: 'thin', scrollbarColor: `${mode === 'light' ? '#CBD5E1' : '#334155'} transparent` },
              '*:focus-visible': { outline: '3px solid rgba(22,143,202,0.25)', outlineOffset: 2 },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                border: `1px solid ${mode === 'light' ? '#E3EAF2' : '#24344A'}`,
                borderRadius: 16,
                boxShadow:
                  mode === 'light'
                    ? '0 1px 2px rgba(16,42,67,0.03), 0 8px 24px rgba(16,42,67,0.045)'
                    : '0 8px 24px rgba(0,0,0,0.28)',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: { textTransform: 'none', fontWeight: 700, borderRadius: 10, minHeight: 38, boxShadow: 'none' },
              containedPrimary: {
                background: 'linear-gradient(135deg, #168FCA, #0C79B1)',
                boxShadow: '0 6px 16px rgba(22,143,202,0.22)',
                '&:hover': { background: 'linear-gradient(135deg, #0C79B1, #08648F)', boxShadow: '0 8px 20px rgba(22,143,202,0.28)' },
              },
              outlined: { borderColor: mode === 'light' ? '#D5DFEA' : '#334155' },
            },
          },
          MuiChip: {
            styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } },
          },
          MuiPaper: {
            styleOverrides: { rounded: { borderRadius: 16 } },
          },
          MuiDialog: {
            styleOverrides: {
              paper: { borderRadius: 18, border: `1px solid ${mode === 'light' ? '#E3EAF2' : '#24344A'}`, boxShadow: '0 24px 70px rgba(16,42,67,0.18)' },
            },
          },
          MuiDialogTitle: {
            styleOverrides: { root: { fontWeight: 800, letterSpacing: '-0.015em' } },
          },
          MuiAlert: {
            styleOverrides: { root: { borderRadius: 12, alignItems: 'center' } },
          },
          MuiTableCell: {
            styleOverrides: {
              head: { fontWeight: 700, color: mode === 'light' ? '#52667A' : '#B8C5D4', backgroundColor: mode === 'light' ? '#F7F9FC' : '#162337' },
              root: { borderColor: mode === 'light' ? '#EDF1F6' : '#24344A' },
            },
          },
          MuiTabs: {
            styleOverrides: { root: { minHeight: 42 }, indicator: { height: 3, borderRadius: '3px 3px 0 0' } },
          },
          MuiTab: {
            styleOverrides: { root: { minHeight: 42, textTransform: 'none', fontWeight: 700, fontSize: 13 } },
          },
          // Match the tenant app: shrunk labels everywhere, opaque background
          // on outlined labels, and proper top padding inside DialogContent
          // so floating labels of the first form input aren't clipped by the
          // DialogTitle.
          MuiTextField: {
            defaultProps: {
              variant: 'outlined',
              InputLabelProps: { shrink: true },
            },
          },
          MuiSelect:      { defaultProps: { variant: 'outlined' } },
          MuiFormControl: { defaultProps: { variant: 'outlined' } },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: 10,
                backgroundColor: mode === 'light' ? '#FFFFFF' : '#111C2E',
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9AB1C5' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
              },
              notchedOutline: { legend: { fontSize: '0.85em' } },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              outlined: {
                backgroundColor: mode === 'light' ? '#ffffff' : '#111827',
                paddingLeft: 4,
                paddingRight: 4,
              },
            },
          },
          MuiDialogContent: {
            styleOverrides: {
              root: { paddingTop: '20px !important' },
            },
          },
          MuiTooltip: { defaultProps: { arrow: true, enterDelay: 450 } },
        },
      }),
    [mode],
  )

  return (
    <ThemeContext.Provider value={{ mode, toggleMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
